import type { RouteRecordRaw, RouteComponent, RouteMeta } from "vue-router";

export interface CrudRouteConfig {
  path: string; // kebab-case, no leading slash e.g. "scholars"
  name?: string; // defaults to path
  listComponent: RouteComponent;
  formComponent: RouteComponent;
  /** Pass true when the edit route must have a required :id (no ?) */
  editIdRequired?: boolean;
  extra?: RouteRecordRaw[]; // e.g. history/:id routes
  /**
   * Applied to every record this helper returns, `extra` included. Inheriting
   * is the fail-safe default: a satellite route added to a gated CRUD block
   * (e.g. `/users/notifications` under the super-admin-only users block) would
   * otherwise ship ungated, and nothing in the type system would say so.
   * An `extra` record that sets its own meta keys overrides the inherited ones.
   */
  meta?: RouteMeta;
}

export function crudRoutes(config: CrudRouteConfig): RouteRecordRaw[] {
  const name = config.name ?? config.path;
  const editIdParam = config.editIdRequired ? ":id" : ":id?";
  const meta = config.meta;
  return [
    {
      path: `/${config.path}`,
      name,
      component: config.listComponent,
      meta,
    },
    {
      path: `/${config.path}/edit/${editIdParam}`,
      name: `${name}-edit`,
      component: config.formComponent,
      props: true,
      meta,
    },
    {
      path: `/${config.path}/new`,
      name: `${name}-new`,
      component: config.formComponent,
      props: true,
      meta,
    },
    ...(config.extra ?? []).map((route) =>
      meta ? { ...route, meta: { ...meta, ...route.meta } } : route,
    ),
  ];
}
